import type { Dispatch, Reducer, UnknownAction } from '@reduxjs/toolkit'

import type { ActionDescriptor } from '../actions/types'
import type { LiveHandler } from '../live/types'
import type { ModulePersistenceEntry, SliceDeclaration } from '../persistence/types'
import type { WindowDescriptor } from '../windows/types'
import type { ModuleShell } from './shell'

/**
 * Публичный договор пакета с приложением (D06, F03, G3).
 *
 * Пакет (`platform-ui`, `resource-onec-ui`, `resource-aqsi-ui`) экспортирует
 * один `UiModule`: reducer своего состояния, объявления сроков жизни, окна,
 * записи хранения и listeners. Endpoints пакет добавляет сам — через
 * `baseApi.injectEndpoints` при импорте своего `api/`. Приложение подключает
 * модуль одной строкой в composition и НЕ правит его внутренностей.
 *
 * `S` — состояние модуля. RootState пакет не знает и знать не может: eslint
 * запрещает импорт приложения, и пакет компилируется без него (D-051).
 */
export interface UiModule<S> {
  /** Ключ модуля в RootState и префикс его окон/записей: `platform`, `onec`, `aqsi`. */
  id: string
  reducer: Reducer<S>
  /** Срок жизни каждого слайса модуля (D-070). */
  slices: readonly SliceDeclaration[]
  windows: readonly WindowDescriptor<S>[]
  persistence: readonly ModulePersistenceEntry<S>[]
  listeners: readonly ModuleListener<S>[]
  /**
   * Обработчики событий живого канала (LIVE): тип события → что перечитать.
   * Нет — пакет событий не слушает; опрос по таймеру остаётся его делом.
   */
  live?: readonly LiveHandler[]
  /**
   * Реестр действий пакета (UI-ACTIONS): что делает каждая кнопка его окон —
   * для подсказки ⓘ. Пакет без реестра подсказок не показывает; правило eslint
   * `krp/action-info` включается для пакета вместе с реестром.
   */
  actions?: readonly ActionDescriptor[]
  /** Вклад в оболочку приложения (RP-SEP5): уведомления, присутствие. Оболочка вида не знает. */
  shell?: ModuleShell<S>
}

/** Что listener пакета получает от composition: свой срез, dispatch и отмену. */
export interface ModuleListenerApi<S> {
  dispatch: Dispatch<UnknownAction>
  getModuleState: () => S
  signal: AbortSignal
}

/** Listener пакета: команда (plain action) → сценарий через порты (F05). */
export interface ModuleListener<S> {
  predicate: (action: UnknownAction) => boolean
  effect: (action: UnknownAction, api: ModuleListenerApi<S>) => void | Promise<void>
}

/** Объявить listener с типизированным действием. */
export function defineListener<S, A extends UnknownAction>(
  match: (action: unknown) => action is A,
  effect: (action: A, api: ModuleListenerApi<S>) => void | Promise<void>,
): ModuleListener<S> {
  return {
    predicate: (action) => match(action),
    effect: (action, api) => (match(action) ? effect(action, api) : undefined),
  }
}
